import React from 'react';
import styled from 'styled-components';
import {
  colors,
  fonts,
  breakpoints,
  radii,
  headingStyle,
  sectionPaddingStyle,
  containerStyle,
} from '../annuaire/designSystem';

const Section = styled.section`
  margin: 0;
  ${sectionPaddingStyle}
  background-color: ${colors.white};
`;

const Container = styled.div`
  ${containerStyle}
`;

const Heading = styled.h2`
  ${headingStyle}
  font-size: 1.5rem;
  margin-bottom: 40px;

  @media (min-width: ${breakpoints.mobile}) {
    font-size: 2.125rem;
  }
`;

const Emphasis = styled.span`
  color: ${colors.bleu};
`;

// ---- Aggregate Rating ----

const ReviewsHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  margin-bottom: 40px;

  @media (min-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr 1fr;
    align-items: center;
  }
`;

const RatingLarge = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const RatingNumber = styled.div`
  font-family: ${fonts.heading};
  font-size: 2.75rem;
  font-weight: 700;
  color: ${colors.textDark};
  line-height: 1;
`;

const RatingInfo = styled.div`
  h3 {
    font-family: ${fonts.body};
    font-weight: 600;
    font-size: 1.5rem;
    margin-bottom: 8px;
    color: ${colors.textDark};
  }
`;

const RatingStars = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
  font-size: 1.5rem;
  color: #f59e0b;
`;

const RatingCount = styled.div`
  font-family: ${fonts.body};
  font-size: 0.9rem;
  color: ${colors.textSecondary};
`;

// ---- Review Cards ----

const ReviewsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 40px;

  @media (min-width: ${breakpoints.mobile}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ReviewCard = styled.div`
  background-color: ${colors.vertPastel};
  border-radius: ${radii.card};
  padding: 24px;
`;

const ReviewAuthor = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const AuthorName = styled.strong`
  font-family: ${fonts.body};
  color: ${colors.textDark};
  font-weight: 600;
`;

const ReviewStars = styled.div`
  display: flex;
  gap: 2px;
  font-size: 1rem;
  color: #f59e0b;
`;

const ReviewDate = styled.div`
  font-family: ${fonts.body};
  font-size: 0.8rem;
  color: ${colors.textSecondary};
`;

const ReviewText = styled.p`
  font-family: ${fonts.body};
  font-size: 0.9rem;
  color: ${colors.textDark};
  margin-top: 12px;
  line-height: 1.5;
`;

// ---- Helpers ----

const DATE_PATTERNS = [
  { regex: /^a day ago$/i, fr: 'il y a 1 jour' },
  {
    regex: /^(\d+) days? ago$/i,
    fr: (m) => `il y a ${m[1]} jour${m[1] > 1 ? 's' : ''}`,
  },
  { regex: /^a week ago$/i, fr: 'il y a 1 semaine' },
  {
    regex: /^(\d+) weeks? ago$/i,
    fr: (m) => `il y a ${m[1]} semaine${m[1] > 1 ? 's' : ''}`,
  },
  { regex: /^a month ago$/i, fr: 'il y a 1 mois' },
  { regex: /^(\d+) months? ago$/i, fr: (m) => `il y a ${m[1]} mois` },
  { regex: /^a year ago$/i, fr: 'il y a 1 an' },
  {
    regex: /^(\d+) years? ago$/i,
    fr: (m) => `il y a ${m[1]} an${m[1] > 1 ? 's' : ''}`,
  },
];

function translateDateToFrench(dateStr) {
  if (!dateStr) return '';
  for (const { regex, fr } of DATE_PATTERNS) {
    const match = dateStr.match(regex);
    if (match) return typeof fr === 'function' ? fr(match) : fr;
  }
  return dateStr;
}

function renderStars(note) {
  const n = Math.round(note || 0);
  return '★'.repeat(Math.min(n, 5)) + '☆'.repeat(Math.max(0, 5 - n));
}

// ---- Component ----

export default function ShopReviews({ shop }) {
  const details = shop.details || {};
  const noteGoogle = details.note_google;
  const nbAvis = details.nb_avis_google;
  const avis = details.avis || [];

  if (!noteGoogle && avis.length === 0) return null;

  // Max 3 reviews displayed
  const displayedReviews = avis.slice(0, 3);

  return (
    <Section>
      <Container>
        <Heading>
          Ce que disent <Emphasis>les cyclistes</Emphasis>
        </Heading>

        {noteGoogle && (
          <ReviewsHeader>
            <RatingLarge>
              <RatingNumber>{noteGoogle}</RatingNumber>
              <RatingInfo>
                <h3>sur 5</h3>
                <RatingStars>{renderStars(noteGoogle)}</RatingStars>
                <RatingCount>{nbAvis || 0} avis Google</RatingCount>
              </RatingInfo>
            </RatingLarge>
          </ReviewsHeader>
        )}

        {displayedReviews.length > 0 && (
          <ReviewsGrid>
            {displayedReviews.map((review, i) => (
              <ReviewCard key={i}>
                <ReviewAuthor>
                  <AuthorName>{review.auteur || 'Anonyme'}</AuthorName>
                  <ReviewStars>{renderStars(review.note)}</ReviewStars>
                </ReviewAuthor>
                {review.date && (
                  <ReviewDate>{translateDateToFrench(review.date)}</ReviewDate>
                )}
                {review.texte && <ReviewText>{review.texte}</ReviewText>}
              </ReviewCard>
            ))}
          </ReviewsGrid>
        )}
      </Container>
    </Section>
  );
}
